import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { CrmStatCard } from '../components/crm/CrmStatCard';
import { CrmKanbanBoard } from '../components/crm/CrmKanbanBoard';
import { CrmContactDrawer } from '../components/crm/CrmContactDrawer';
import { apiService } from '../services/api';
import type { CrmCampaignRef, CrmContact, CrmOverviewResponse, CrmTag } from '../types/crm';

const EMPTY_CONTACT: CrmContact = {
  id: '',
  nome: '',
  telefone: '',
  email: '',
  cidade: '',
  bairro: '',
  origemContato: '',
  interessePrincipal: '',
  tipoCampanha: 'RELACIONAMENTO',
  statusJornada: 'NOVO_CONTATO',
  nivelInteresse: '',
  responsavel: '',
  tags: [],
  observacoes: '',
  consentimentoMensagens: true,
  criadoEm: new Date().toISOString(),
  atualizadoEm: new Date().toISOString(),
  tagItems: [],
  interactions: [],
  tasks: []
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Sem registro';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export function CrmPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<CrmOverviewResponse | null>(null);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [campaigns, setCampaigns] = useState<CrmCampaignRef[]>([]);
  const [selectedContact, setSelectedContact] = useState<CrmContact | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    stage: '',
    city: '',
    interest: '',
    campaignType: ''
  });

  const loadOverview = useCallback(async () => {
    const response = await apiService.getCrmOverview();
    setOverview(response.data);
  }, []);

  const loadTags = useCallback(async () => {
    const response = await apiService.getCrmTags();
    setTags((response.data || []) as CrmTag[]);
  }, []);

  const loadCampaigns = useCallback(async () => {
    const response = await apiService.getCrmCampaigns();
    setCampaigns((response.data || []) as CrmCampaignRef[]);
  }, []);

  const loadContacts = useCallback(async () => {
    const response = await apiService.getCrmContacts({
      search: filters.search || undefined,
      stage: filters.stage || undefined,
      city: filters.city || undefined,
      interest: filters.interest || undefined,
      campaignType: filters.campaignType || undefined,
      pageSize: 80
    });
    setContacts(response.data || []);
  }, [filters]);

  const loadDetail = useCallback(async (contactId: string) => {
    const response = await apiService.getCrmContact(contactId);
    setSelectedContact(response.data);
    setDrawerOpen(true);
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([loadOverview(), loadContacts(), loadTags(), loadCampaigns()]);
    } catch (error) {
      console.error('Erro ao carregar CRM:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar CRM');
    } finally {
      setLoading(false);
    }
  }, [loadOverview, loadContacts, loadTags, loadCampaigns]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const reloadSelectedContact = useCallback(async () => {
    if (!selectedContact?.id) return;
    await loadDetail(selectedContact.id);
  }, [loadDetail, selectedContact?.id]);

  const handleMoveContact = async (contactId: string, stage: string) => {
    try {
      await apiService.updateCrmStage(contactId, stage);
      setContacts((prev) => prev.map((contact) => (contact.id === contactId ? { ...contact, statusJornada: stage as any } : contact)));
      if (selectedContact?.id === contactId) {
        await reloadSelectedContact();
      }
      await loadOverview();
      toast.success('Etapa atualizada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao mover contato');
    }
  };

  const openNewContact = () => {
    setSelectedContact({ ...EMPTY_CONTACT, responsavel: '' });
    setDrawerOpen(true);
  };

  const handleSaveContact = async (payload: Record<string, unknown>, contactId?: string) => {
    try {
      setIsSaving(true);
      if (contactId) {
        await apiService.updateCrmContact(contactId, payload);
        toast.success('Contato atualizado');
        await loadDetail(contactId);
      } else {
        const response = await apiService.createCrmContact(payload);
        const createdId = (response as any)?.data?.id;
        toast.success('Contato criado');
        if (createdId) {
          await loadDetail(createdId);
        } else {
          setDrawerOpen(false);
        }
      }
      await Promise.all([loadContacts(), loadOverview()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar contato');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStage = async (contactId: string, stage: string) => {
    await handleMoveContact(contactId, stage);
  };

  const handleCreateInteraction = async (contactId: string, payload: Record<string, unknown>) => {
    try {
      await apiService.createCrmInteraction(contactId, payload);
      toast.success('Interacao registrada');
      await Promise.all([reloadSelectedContact(), loadOverview(), loadContacts()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao registrar interacao');
    }
  };

  const handleCreateTask = async (contactId: string, payload: Record<string, unknown>) => {
    try {
      await apiService.createCrmTask(contactId, payload);
      toast.success('Tarefa criada');
      await Promise.all([reloadSelectedContact(), loadOverview(), loadContacts()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao criar tarefa');
    }
  };

  const handleAssignTag = async (contactId: string, tagId: string) => {
    try {
      await apiService.assignCrmTag(contactId, tagId);
      toast.success('Tag adicionada');
      await Promise.all([reloadSelectedContact(), loadOverview(), loadContacts()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao adicionar tag');
    }
  };

  const handleRemoveTag = async (contactId: string, tagId: string) => {
    try {
      await apiService.removeCrmTag(contactId, tagId);
      toast.success('Tag removida');
      await Promise.all([reloadSelectedContact(), loadOverview(), loadContacts()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao remover tag');
    }
  };

  const handleLinkCampaign = async (contactId: string, campaignId: string) => {
    try {
      await apiService.linkCrmCampaign(contactId, campaignId);
      toast.success('Campanha vinculada');
      await reloadSelectedContact();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao vincular campanha');
    }
  };

  const dashboardCards = useMemo(() => {
    const cards = overview?.cards;
    if (!cards) return [];

    return [
      ['Total de contatos', cards.totalContacts, 'Base ativa'],
      ['Novos contatos', cards.newContacts, 'Ultimos 7 dias'],
      ['Contatos interessados', cards.interested, 'Jornada atual'],
      ['Em acompanhamento', cards.inFollowUp, 'Follow-up ativo'],
      ['Convertidos', cards.converted, `${cards.conversionRate}% de conversao`],
      ['Sem resposta', cards.noResponse, 'Pedem retomada'],
      ['Sem interesse', cards.noInterest, 'Encerrados'],
      ['Campanhas ativas', cards.activeCampaigns, 'WhatsApp e e-mail'],
      ['Mensagens enviadas', cards.sentMessages, 'Historico consolidado'],
      ['E-mails enviados', cards.sentEmails, 'Interacoes registradas'],
      ['Tarefas vencidas', cards.overdueTasks, 'Alertas de follow-up']
    ];
  }, [overview]);

  return (
    <>
      <Header
        title="CRM"
        subtitle="Gestao de relacionamento, jornada, tarefas e historico em um unico lugar."
        actions={
          <div className="flex flex-wrap gap-3">
            <button
              onClick={refreshAll}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Atualizar
            </button>
            <button
              onClick={() => navigate('/crm/territorial')}
              className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-medium text-indigo-900 hover:bg-indigo-100"
            >
              Visão Territorial
            </button>
            <button
              onClick={openNewContact}
              className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              + Novo contato
            </button>
          </div>
        }
      />

      <div className="space-y-6 p-6">
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {loading && dashboardCards.length === 0
            ? Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white" />
              ))
            : dashboardCards.map(([label, value, hint]) => (
                <CrmStatCard key={String(label)} label={String(label)} value={String(value)} hint={String(hint)} />
              ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Funil / Jornada</h2>
                <p className="text-sm text-slate-500">Arraste os contatos entre as etapas para atualizar o relacionamento.</p>
              </div>
            </div>
            <div className="mt-5">
              <CrmKanbanBoard
                stages={overview?.stages || []}
                contacts={contacts}
                onOpenContact={(contact) => loadDetail(contact.id)}
                onMoveContact={handleMoveContact}
              />
            </div>
          </div>

          <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Leitura rapida</h2>
            <p className="mt-1 text-sm text-slate-500">Contadores por etapa e alertas de relacionamento.</p>

            <div className="mt-5 space-y-3">
              {(overview?.stages || []).map((stage) => (
                <div key={stage.key}>
                  <div className="mb-1 flex items-center justify-between text-sm text-slate-600">
                    <span>{stage.label}</span>
                    <span>{stage.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100">
                    <div
                      className="h-2 rounded-full bg-slate-900"
                      style={{
                        width: `${overview?.cards.totalContacts ? Math.max((stage.count / overview.cards.totalContacts) * 100, 4) : 0}%`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">Alertas de follow-up</p>
              <p className="mt-1 text-sm text-amber-800">
                {overview?.cards.overdueTasks || 0} tarefas estao vencidas e pedem retomada.
              </p>
            </div>
          </aside>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Lista de contatos</h2>
              <p className="text-sm text-slate-500">Busca em tempo real, filtros por etapa, interesse e localidade.</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <input
                value={filters.search}
                onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="Buscar nome, telefone, cidade..."
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              />
              <select
                value={filters.stage}
                onChange={(event) => setFilters((prev) => ({ ...prev, stage: event.target.value }))}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              >
                <option value="">Todas as etapas</option>
                {(overview?.filters.stages || []).map((stage) => (
                  <option key={stage.key} value={stage.key}>{stage.label}</option>
                ))}
              </select>
              <select
                value={filters.city}
                onChange={(event) => setFilters((prev) => ({ ...prev, city: event.target.value }))}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              >
                <option value="">Todas as cidades</option>
                {(overview?.filters.cities || []).map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <select
                value={filters.interest}
                onChange={(event) => setFilters((prev) => ({ ...prev, interest: event.target.value }))}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              >
                <option value="">Todos os interesses</option>
                {(overview?.filters.interests || []).map((interest) => (
                  <option key={interest} value={interest}>{interest}</option>
                ))}
              </select>
              <select
                value={filters.campaignType}
                onChange={(event) => setFilters((prev) => ({ ...prev, campaignType: event.target.value }))}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              >
                <option value="">Todos os tipos</option>
                {(overview?.filters.campaignTypes || []).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Contato</th>
                  <th className="px-4 py-3">Jornada</th>
                  <th className="px-4 py-3">Interesse</th>
                  <th className="px-4 py-3">Localidade</th>
                  <th className="px-4 py-3">Responsavel</th>
                  <th className="px-4 py-3">Ultimo contato</th>
                  <th className="px-4 py-3">Proxima acao</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    onClick={() => loadDetail(contact.id)}
                    className="cursor-pointer transition-colors hover:bg-slate-50"
                  >
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-semibold text-slate-900">{contact.nome}</p>
                        <p className="text-slate-500">{contact.telefone}</p>
                        <p className="text-slate-500">{contact.email || 'Sem e-mail'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {(overview?.stages || []).find((stage) => stage.key === contact.statusJornada)?.label || contact.statusJornada}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-slate-600">{contact.interessePrincipal || contact.interestLabel || 'Nao definido'}</td>
                    <td className="px-4 py-4 text-slate-600">{[contact.bairro, contact.cidade].filter(Boolean).join(' / ') || 'Nao informado'}</td>
                    <td className="px-4 py-4 text-slate-600">{contact.responsavel || 'Nao definido'}</td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTime(contact.ultimoContato || contact.lastInteraction?.criadoEm)}</td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTime(contact.proximaAcao || contact.nextTask?.dataAgendada)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {contacts.length === 0 && !loading ? (
              <div className="py-10 text-center text-sm text-slate-400">Nenhum contato encontrado com os filtros aplicados.</div>
            ) : null}
          </div>
        </section>
      </div>

      <CrmContactDrawer
        isOpen={drawerOpen}
        contact={selectedContact}
        availableTags={tags}
        campaigns={campaigns}
        onClose={() => setDrawerOpen(false)}
        onSaveContact={handleSaveContact}
        onUpdateStage={handleUpdateStage}
        onCreateInteraction={handleCreateInteraction}
        onCreateTask={handleCreateTask}
        onAssignTag={handleAssignTag}
        onRemoveTag={handleRemoveTag}
        onLinkCampaign={handleLinkCampaign}
      />

      {isSaving ? (
        <div className="fixed bottom-6 right-6 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-lg">
          Salvando alteracoes...
        </div>
      ) : null}
    </>
  );
}
